/**
 * Read ESC/POS bytes back into what a printer would do (T07-35 bench without hardware): the mock
 * printer, the settings' 「テスト印刷」 check and the unit tests use it to verify the bytes pos-service
 * sent — raster bands fit the paper (384 / 576 dots), a QR code is there, the paper is cut, the
 * drawer kicks only for cash. Covers the commands pos-service and the tablet emit
 * (`app/printing/escpos.py`, `app/services/receipt/escpos.py`, `escpos.ts`); anything else is
 * reported as `unknown` (never silently skipped).
 */

export type EscPosOp =
  | { kind: "init" }
  | { kind: "text"; bytes: number }
  | { kind: "align"; value: number }
  | { kind: "bold"; on: boolean }
  | { kind: "size"; value: number }
  | { kind: "feed"; lines: number }
  | { kind: "cut" }
  | { kind: "drawer"; pin: number }
  | { kind: "raster"; widthBytes: number; rows: number }
  | { kind: "qr"; bytes: number }
  | { kind: "charset"; value: number }
  | { kind: "kanji"; on: boolean }
  | { kind: "unknown"; at: number; byte: number };

export interface EscPosSummary {
  ops: EscPosOp[];
  /** Dots per line of the raster bands (all bands must agree). */
  rasterDots: number | null;
  rasterRows: number;
  textBytes: number;
  cut: boolean;
  drawer: boolean;
  qr: boolean;
  unknown: number;
}

export function decodeEscPos(data: Uint8Array): EscPosSummary {
  const ops: EscPosOp[] = [];
  let i = 0;
  let text = 0;
  const flushText = () => {
    if (text) {
      ops.push({ kind: "text", bytes: text });
      text = 0;
    }
  };
  const at = (n: number) => data[n] ?? 0;
  while (i < data.length) {
    const b = at(i);
    if (b === 0x1b) {
      flushText();
      const c = at(i + 1);
      if (c === 0x40) {
        ops.push({ kind: "init" });
        i += 2;
      } else if (c === 0x61) {
        ops.push({ kind: "align", value: at(i + 2) });
        i += 3;
      } else if (c === 0x45) {
        ops.push({ kind: "bold", on: at(i + 2) !== 0 });
        i += 3;
      } else if (c === 0x64) {
        ops.push({ kind: "feed", lines: at(i + 2) });
        i += 3;
      } else if (c === 0x70) {
        ops.push({ kind: "drawer", pin: at(i + 2) });
        i += 5;
      } else if (c === 0x52) {
        ops.push({ kind: "charset", value: at(i + 2) });
        i += 3;
      } else {
        ops.push({ kind: "unknown", at: i, byte: c });
        i += 2;
      }
    } else if (b === 0x1d) {
      flushText();
      const c = at(i + 1);
      if (c === 0x76 && at(i + 2) === 0x30) {
        const widthBytes = at(i + 4) + 256 * at(i + 5);
        const rows = at(i + 6) + 256 * at(i + 7);
        ops.push({ kind: "raster", widthBytes, rows });
        i += 8 + widthBytes * rows;
      } else if (c === 0x56) {
        ops.push({ kind: "cut" });
        i += at(i + 2) === 0x42 || at(i + 2) === 0x41 ? 4 : 3;
      } else if (c === 0x21) {
        ops.push({ kind: "size", value: at(i + 2) });
        i += 3;
      } else if (c === 0x28 && at(i + 2) === 0x6b) {
        const length = at(i + 3) + 256 * at(i + 4);
        ops.push({ kind: "qr", bytes: length });
        i += 5 + length;
      } else {
        ops.push({ kind: "unknown", at: i, byte: c });
        i += 2;
      }
    } else if (b === 0x1c) {
      flushText();
      const c = at(i + 1);
      if (c === 0x26) {
        ops.push({ kind: "kanji", on: true });
        i += 2;
      } else if (c === 0x2e) {
        ops.push({ kind: "kanji", on: false });
        i += 2;
      } else if (c === 0x43) {
        i += 3; // FS C n — kanji code system
      } else {
        ops.push({ kind: "unknown", at: i, byte: c });
        i += 2;
      }
    } else {
      text += 1;
      i += 1;
    }
  }
  flushText();
  const bands = ops.filter((o): o is Extract<EscPosOp, { kind: "raster" }> => o.kind === "raster");
  const widths = new Set(bands.map((band) => band.widthBytes * 8));
  return {
    ops,
    rasterDots: widths.size === 1 ? ([...widths][0] ?? null) : widths.size ? -1 : null,
    rasterRows: bands.reduce((sum, band) => sum + band.rows, 0),
    textBytes: ops.reduce((sum, o) => sum + (o.kind === "text" ? o.bytes : 0), 0),
    cut: ops.some((o) => o.kind === "cut"),
    drawer: ops.some((o) => o.kind === "drawer"),
    qr: ops.some((o) => o.kind === "qr"),
    unknown: ops.filter((o) => o.kind === "unknown").length,
  };
}
